import { CSSProperties, RefObject, useCallback, useRef } from 'react';
import styled from 'styled-components';

// Utils
import combineURLs from 'axios/lib/helpers/combineURLs';

// Hooks
import { useIntersectionObserver } from 'hooks/use-intersection-observer';

export interface TOPImageProps {
  alt?: string;
  className?: string;
  lazyLoad?: boolean;
  src: string;
  style?: CSSProperties;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

/**
 * When lazy loading is enabled, the image element will initially be mounted with its URL path assigned to `data-src`
 * instead of the standard `src` attribute. The image will also be assigned a `ref` that will be tracked by an
 * IntersectionObserver. When the image intersects with the observer, the `data-src` value will be copied to the
 * element's `src` attribute, which will trigger the loading of the image asset.
 *
 * NOTE: Lazy loading should not be applied to all images by default. Rather, it should only be applied to images that
 * are likely to be "below the fold" on page load. See: https://web.dev/lazy-loading-best-practices/
 */
export const TOPImage: React.FC<TOPImageProps> = ({
  alt = '',
  className = '',
  lazyLoad = false,
  src,
  style,
  onError = () => {},
}) => {
  const imgRef = useRef<HTMLImageElement>();

  const lazyLoadCallback = useCallback(() => {
    const image = imgRef.current;
    if (image) {
      image.src = image.dataset.src ?? '';
      delete image.dataset.src;
    }
  }, []);

  const imgSrc = src.includes('http') ? src : combineURLs(process.env.REACT_APP_STATIC_ROOT ?? '', src);

  useIntersectionObserver(imgRef as RefObject<HTMLImageElement>, lazyLoadCallback);

  return (
    <TOPImageWrapper className={className} style={style}>
      <TOPImageSpacer aria-hidden="true" />
      <picture>
        <img
          alt={alt}
          data-src={lazyLoad ? imgSrc : undefined}
          ref={lazyLoad ? (imgRef as RefObject<HTMLImageElement>) : undefined}
          src={lazyLoad ? undefined : imgSrc}
          onError={onError}
        />
      </picture>
    </TOPImageWrapper>
  );
};

const TOPImageWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;

  img {
    height: 100%;
    left: 0;
    object-fit: cover;
    object-position: center;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

const TOPImageSpacer = styled.div`
  background: var(--color-neutral__accent-background);
  height: 100%;
  padding-bottom: 100%;
  width: 100%;
`;
